import React, { useEffect, useState } from "react";
import AppLayout, { PageHeader } from "../components/AppLayout";
import {
    listOrgUsers,
    inviteUser,
    updateOrgUser,
    getActivity,
} from "../lib/api";
import { useAuth, roleAtLeast, formatApiError } from "../lib/auth";
import { toast } from "sonner";
import {
    UserPlus,
    Loader2,
    ShieldCheck,
    Pause,
    Play,
    ClipboardList,
    Copy,
} from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";

const ROLES = ["Admin", "QAManager", "QAAuditor", "SystemOwner", "Observer"];

export default function Settings() {
    const { user } = useAuth();
    const canManage = roleAtLeast(user.role, "QAManager");
    const [users, setUsers] = useState([]);
    const [activity, setActivity] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [u, a] = await Promise.all([listOrgUsers(), getActivity({ limit: 80 })]);
            setUsers(u);
            setActivity(a);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    return (
        <AppLayout>
            <PageHeader
                eyebrow="Settings"
                title="Team & activity"
                sub="Manage users and roles, review your organization's activity log."
                right={canManage && <InviteDialog onInvited={load} currentUserRole={user.role} />}
            />

            <div className="p-8 md:p-12 grid grid-cols-1 xl:grid-cols-3 gap-8">
                {/* Team table */}
                <div className="xl:col-span-2">
                    <div className="overline mb-3">Team members</div>
                    <div className="border border-hair bg-white">
                        <div className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-hair overline text-[10px]">
                            <div className="col-span-4">Name / Email</div>
                            <div className="col-span-2">Role</div>
                            <div className="col-span-2">Status</div>
                            <div className="col-span-2">Last login</div>
                            <div className="col-span-2 text-right">Actions</div>
                        </div>
                        {loading && (
                            <div className="p-10 text-center text-neutral-500">
                                <Loader2 className="animate-spin inline" size={16} />
                            </div>
                        )}
                        {!loading && users.length === 0 && (
                            <div className="p-10 text-center text-neutral-500 text-sm">
                                No teammates yet. Invite someone above.
                            </div>
                        )}
                        {users.map((u) => (
                            <UserRow
                                key={u.id}
                                row={u}
                                self={u.id === user.id}
                                canManage={canManage}
                                currentUserRole={user.role}
                                onChange={load}
                            />
                        ))}
                    </div>
                </div>

                {/* Activity */}
                <div>
                    <div className="overline mb-3 flex items-center gap-2">
                        <ClipboardList size={12} /> Recent activity
                    </div>
                    <div className="border border-hair bg-white max-h-[640px] overflow-y-auto">
                        {activity.length === 0 && (
                            <div className="p-8 text-center text-sm text-neutral-500">
                                No activity yet.
                            </div>
                        )}
                        {activity.map((a) => (
                            <ActivityEntry key={a.id} a={a} />
                        ))}
                    </div>
                </div>
            </div>
        </AppLayout>
    );
}

const UserRow = ({ row, self, canManage, currentUserRole, onChange }) => {
    const [busy, setBusy] = useState(false);
    const change = async (patch) => {
        setBusy(true);
        try {
            await updateOrgUser(row.id, patch);
            toast.success("Updated");
            onChange?.();
        } catch (e) {
            toast.error(formatApiError(e, "Update failed"));
        } finally {
            setBusy(false);
        }
    };
    return (
        <div
            className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-hair last:border-b-0 items-center"
            data-testid={`user-row-${row.email}`}
        >
            <div className="col-span-4 min-w-0">
                <div className="text-sm heading font-medium truncate">
                    {row.name || row.email}
                    {self && (
                        <span className="ml-2 mono text-[10px] text-[#C2410C]">
                            (you)
                        </span>
                    )}
                </div>
                <div className="text-xs text-neutral-500 mono truncate">
                    {row.email}
                </div>
            </div>
            <div className="col-span-2">
                {canManage && !self ? (
                    <Select
                        value={row.role}
                        onValueChange={(v) => change({ role: v })}
                        disabled={busy}
                    >
                        <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {ROLES.map((r) => (
                                <SelectItem
                                    key={r}
                                    value={r}
                                    disabled={r === "Admin" && currentUserRole !== "Admin"}
                                >
                                    {r}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                ) : (
                    <span className="mono text-xs">{row.role}</span>
                )}
            </div>
            <div className="col-span-2">
                <StatusChip value={row.status} />
            </div>
            <div className="col-span-2 mono text-[11px] text-neutral-500">
                {row.last_login_at
                    ? new Date(row.last_login_at).toLocaleDateString()
                    : row.invited_at
                      ? `invited ${new Date(row.invited_at).toLocaleDateString()}`
                      : "—"}
            </div>
            <div className="col-span-2 flex justify-end">
                {canManage && !self && (
                    <button
                        onClick={() =>
                            change({
                                status:
                                    row.status === "active" ? "disabled" : "active",
                            })
                        }
                        disabled={busy}
                        className="text-xs inline-flex items-center gap-1 border border-hair px-2 py-1 rounded-sm hover:bg-[#fafafa] disabled:opacity-50"
                        data-testid={`toggle-${row.email}`}
                    >
                        {row.status === "active" ? (
                            <>
                                <Pause size={10} /> Disable
                            </>
                        ) : (
                            <>
                                <Play size={10} /> Enable
                            </>
                        )}
                    </button>
                )}
            </div>
        </div>
    );
};

const StatusChip = ({ value }) => {
    const cls =
        value === "active"
            ? "verdict-pass"
            : value === "invited"
              ? "verdict-conditional"
              : "verdict-missing";
    return (
        <span
            className={`mono inline-flex items-center px-2 py-[2px] text-[10px] tracking-wider border rounded-sm ${cls}`}
        >
            {value}
        </span>
    );
};

const ActivityEntry = ({ a }) => {
    const verb = a.action?.replace(/[._]/g, " ");
    return (
        <div className="px-4 py-3 border-b border-hair last:border-b-0 text-[12px]">
            <div className="flex items-center justify-between gap-2">
                <span className="heading font-medium truncate">
                    {a.user_name || a.user_email}
                </span>
                <span className="mono text-[10px] text-neutral-500">
                    {new Date(a.created_at).toLocaleString()}
                </span>
            </div>
            <div className="text-neutral-600 mt-1">
                <span className="mono text-[11px] text-[#C2410C]">{verb}</span>
                {a.resource_type && (
                    <span className="mono text-[11px] text-neutral-500 ml-2">
                        {a.resource_type}
                    </span>
                )}
            </div>
        </div>
    );
};

const InviteDialog = ({ onInvited, currentUserRole }) => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        email: "",
        name: "",
        role: "QAAuditor",
    });
    const [busy, setBusy] = useState(false);
    const [inviteLink, setInviteLink] = useState(null);

    const submit = async () => {
        if (!form.email.trim() || !form.name.trim()) {
            toast.error("Email and name required");
            return;
        }
        setBusy(true);
        try {
            const r = await inviteUser(form);
            const link = `${window.location.origin}/accept-invite?token=${r.invitation_token}`;
            setInviteLink(link);
            toast.success(`${r.email} invited`);
            onInvited?.();
        } catch (e) {
            toast.error(formatApiError(e, "Invite failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                setOpen(v);
                if (!v) {
                    setForm({ email: "", name: "", role: "QAAuditor" });
                    setInviteLink(null);
                }
            }}
        >
            <DialogTrigger asChild>
                <button
                    data-testid="btn-invite"
                    className="inline-flex items-center gap-2 bg-[#C2410C] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#9A3412]"
                >
                    <UserPlus size={14} /> Invite teammate
                </button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="heading tracking-tight">
                        Invite teammate
                    </DialogTitle>
                </DialogHeader>
                {inviteLink ? (
                    <div className="space-y-4 pt-2">
                        <p className="text-sm text-neutral-600">
                            Copy this link and send it to your teammate. It expires
                            in 14 days.
                        </p>
                        <div className="border border-hair bg-[#fafafa] p-3 mono text-[11px] break-all flex items-center gap-2">
                            <code className="flex-1">{inviteLink}</code>
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(inviteLink);
                                    toast.success("Copied");
                                }}
                                className="text-neutral-500 hover:text-[#0a0a0a] shrink-0"
                            >
                                <Copy size={12} />
                            </button>
                        </div>
                        <div className="text-[11px] text-neutral-500 mono">
                            Email delivery is stubbed in this build — hook up
                            Resend/SendGrid later.
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4 pt-2">
                        <div>
                            <Label className="text-xs overline">Work email</Label>
                            <Input
                                className="mt-1"
                                type="email"
                                value={form.email}
                                onChange={(e) =>
                                    setForm({ ...form, email: e.target.value })
                                }
                                data-testid="invite-email"
                            />
                        </div>
                        <div>
                            <Label className="text-xs overline">Full name</Label>
                            <Input
                                className="mt-1"
                                value={form.name}
                                onChange={(e) =>
                                    setForm({ ...form, name: e.target.value })
                                }
                                data-testid="invite-name"
                            />
                        </div>
                        <div>
                            <Label className="text-xs overline">Role</Label>
                            <Select
                                value={form.role}
                                onValueChange={(v) => setForm({ ...form, role: v })}
                            >
                                <SelectTrigger className="mt-1">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {ROLES.map((r) => (
                                        <SelectItem
                                            key={r}
                                            value={r}
                                            disabled={
                                                r === "Admin" &&
                                                currentUserRole !== "Admin"
                                            }
                                        >
                                            {r}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <DialogFooter>
                            <button
                                data-testid="btn-invite-submit"
                                onClick={submit}
                                disabled={busy}
                                className="inline-flex items-center gap-2 bg-[#C2410C] text-white text-sm px-4 py-2 rounded-sm disabled:opacity-50"
                            >
                                {busy && (
                                    <Loader2 size={14} className="animate-spin" />
                                )}
                                <ShieldCheck size={14} /> Send invite
                            </button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
